import { useEffect, useMemo, useState, type JSX } from "react";
import {
  RESEARCH_NODES,
  RESEARCH_TREES,
  researchNodeStatus,
  type ResearchNode,
  type ResearchNodeStatus,
} from "@stahlfront/core";
import { fetchResearchList, startResearch } from "../api.js";
import { useStore } from "../store.js";

/**
 * §10 / §6.4.4 — Research tree screen, MVP cut.
 *
 * Hex menu (per spec full vision) is deferred to Phase 2.F.2; this version
 * lays nodes out as a tier-grouped vertical list — same data + same status
 * lifecycle, so the migration to the hex layout in 2.F.2 is purely visual.
 */
export function ResearchPanel(): JSX.Element | null {
  const open = useStore((s) => s.researchPanelOpen);
  const setOpen = useStore((s) => s.setResearchPanelOpen);
  const me = useStore((s) => s.me);
  const setMe = useStore((s) => s.setMe);
  const researchJobs = useStore((s) => s.researchJobs);
  const completedNodeIds = useStore((s) => s.completedNodeIds);
  const slots = useStore((s) => s.researchSlots);
  const setResearchJobs = useStore((s) => s.setResearchJobs);
  const setCompletedNodeIds = useStore((s) => s.setCompletedNodeIds);
  const setResearchSlots = useStore((s) => s.setResearchSlots);
  const setSpeedupConfirm = useStore((s) => s.setSpeedupConfirm);

  const [pendingNodeId, setPendingNodeId] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  // Force a 1Hz re-render so live ETAs in active rows stay fresh.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !pendingNodeId) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pendingNodeId, setOpen]);

  // O(1) status lookups — built once per render via memoised sets.
  const statusSets = useMemo(() => {
    const completed = new Set(completedNodeIds);
    const active = new Set(researchJobs.map((j) => j.nodeId));
    return { completed, active };
  }, [completedNodeIds, researchJobs]);

  const nodesByTier = useMemo(() => {
    const buckets = new Map<number, ResearchNode[]>();
    for (const n of RESEARCH_NODES) {
      const list = buckets.get(n.tier) ?? [];
      list.push(n);
      buckets.set(n.tier, list);
    }
    return Array.from(buckets.entries()).sort((a, b) => a[0] - b[0]);
  }, []);

  const tree = RESEARCH_TREES.INDUSTRIAL_PROGRESS;

  const onStart = async (node: ResearchNode): Promise<void> => {
    if (pendingNodeId || !me) return;
    setPendingNodeId(node.id);
    setErrorText(null);
    try {
      const res = await startResearch({ nodeId: node.id });
      setMe({ ...me, wallet: res.wallet });
      const list = await fetchResearchList();
      setResearchJobs(list.activeJobs);
      setCompletedNodeIds(list.completedNodeIds);
      setResearchSlots(list.slots);
    } catch (err) {
      const m = String(err);
      if (m.includes("INSUFFICIENT_TREASURY")) setErrorText("Not enough treasury.");
      else if (m.includes("INSUFFICIENT_RESERVES")) setErrorText("Not enough reserves.");
      else if (m.includes("NO_RESEARCH_SLOTS")) setErrorText("All research slots are busy.");
      else if (m.includes("PREREQ_NOT_MET")) setErrorText("A prerequisite isn't complete yet.");
      else if (m.includes("ALREADY_ACTIVE")) setErrorText("This node is already in progress.");
      else if (m.includes("ALREADY_COMPLETE")) setErrorText("Already researched.");
      else setErrorText("Couldn't start research. Try again.");
    } finally {
      setPendingNodeId(null);
    }
  };

  const onSpeedup = (jobId: string): void => {
    setSpeedupConfirm({ kind: "research", jobId });
  };

  if (!open || !me) return null;

  const accent = `oklch(58% 0.14 ${tree.accentHue})`;
  const accentSoft = `oklch(28% 0.05 ${tree.accentHue})`;
  const accentTitle = `oklch(36% 0.10 ${tree.accentHue})`;

  return (
    <div
      className="rsp-backdrop"
      onPointerDown={() => !pendingNodeId && setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rsp-title"
    >
      <div
        className="rsp-card"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          ["--rsp-accent" as string]: accent,
          ["--rsp-accent-soft" as string]: accentSoft,
          ["--rsp-accent-title" as string]: accentTitle,
        }}
      >
        <header className="rsp-titlebar">
          <span className="rsp-title-left">
            <span id="rsp-title" className="rsp-title-name">{tree.displayName}</span>
            <span className="rsp-title-meta">
              Slots {slots.used} / {slots.max}
            </span>
          </span>
          <button
            type="button"
            className="rsp-close"
            aria-label="Close research panel"
            onClick={() => setOpen(false)}
          >
            ×
          </button>
        </header>

        <p className="rsp-tree-lore">{tree.lore}</p>

        {errorText ? (
          <p className="rsp-error" role="alert">{errorText}</p>
        ) : null}

        <div className="rsp-body" aria-label="Research nodes by tier">
          {nodesByTier.map(([tier, nodes]) => (
            <section key={tier} className="rsp-tier">
              <h3 className="rsp-tier-head">Tier {tier}</h3>
              <ul className="rsp-tier-list">
                {nodes.map((n) => {
                  const status = researchNodeStatus(
                    n.id,
                    statusSets.completed,
                    statusSets.active,
                  );
                  const job = researchJobs.find((j) => j.nodeId === n.id);
                  return (
                    <ResearchNodeCard
                      key={n.id}
                      node={n}
                      status={status}
                      jobId={job?.id ?? null}
                      etaMs={job ? Math.max(0, job.startedAtMs + job.durationMs - Date.now()) : 0}
                      pending={pendingNodeId === n.id}
                      onStart={() => void onStart(n)}
                      onSpeedup={(id) => onSpeedup(id)}
                    />
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <footer className="rsp-footer">
          <span className="rsp-footer-meta">
            {slots.max === 0
              ? "Build a Research Center to unlock research."
              : "Slots grow with Research Center level: 1 / 2 / 3 / 4 at Lv 1 / 5 / 10 / 20."}
          </span>
          <button type="button" onClick={() => setOpen(false)} className="rsp-footer-ok">
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

interface NodeCardProps {
  readonly node: ResearchNode;
  readonly status: ResearchNodeStatus;
  readonly jobId: string | null;
  readonly etaMs: number;
  readonly pending: boolean;
  readonly onStart: () => void;
  readonly onSpeedup: (jobId: string) => void;
}

function ResearchNodeCard({
  node,
  status,
  jobId,
  etaMs,
  pending,
  onStart,
  onSpeedup,
}: NodeCardProps): JSX.Element {
  return (
    <li className={`rsp-node rsp-node-${status}`}>
      <header className="rsp-node-head">
        <span className="rsp-node-name">{node.displayName}</span>
        <StatusPill status={status} />
      </header>
      <p className="rsp-node-effect">{node.effect}</p>
      <dl className="rsp-node-stats">
        <div className="rsp-node-stat">
          <dt>Treasury</dt>
          <dd>{formatNumber(node.costTreasury)}</dd>
        </div>
        {node.costReserves > 0 ? (
          <div className="rsp-node-stat">
            <dt>Reserves</dt>
            <dd>{formatNumber(node.costReserves)}</dd>
          </div>
        ) : null}
        <div className="rsp-node-stat">
          <dt>Duration</dt>
          <dd>{formatDuration(node.durationMs)}</dd>
        </div>
        {status === "active" && jobId ? (
          <div className="rsp-node-stat rsp-node-stat-eta">
            <dt>ETA</dt>
            <dd>{formatDuration(etaMs)}</dd>
          </div>
        ) : null}
      </dl>
      {node.prereqs.length > 0 ? (
        <p className="rsp-node-prereqs">
          Prereqs: {node.prereqs.map(prereqDisplayName).join(", ")}
        </p>
      ) : null}
      <div className="rsp-node-actions">
        {status === "complete" ? (
          <span className="rsp-node-done" aria-label="Researched">✓ Researched</span>
        ) : status === "active" && jobId ? (
          <button
            type="button"
            className="rsp-node-cta-speedup"
            onClick={() => onSpeedup(jobId)}
            title="Spend diamonds to finish now"
          >
            Speed-Up
          </button>
        ) : status === "available" ? (
          <button
            type="button"
            className="rsp-node-cta-start"
            onClick={onStart}
            disabled={pending}
            aria-busy={pending}
          >
            {pending ? <><span className="rsp-spinner" aria-hidden="true" /> Starting…</> : "Start research"}
          </button>
        ) : (
          <span className="rsp-node-locked" aria-label="Locked">Locked</span>
        )}
      </div>
    </li>
  );
}

function StatusPill({ status }: { readonly status: ResearchNodeStatus }): JSX.Element {
  const label =
    status === "complete" ? "Done" : status === "active" ? "Active" : status === "available" ? "Available" : "Locked";
  return <span className={`rsp-status-pill rsp-status-pill-${status}`}>{label}</span>;
}

function prereqDisplayName(id: string): string {
  // Catalog is small; lookup against the same module.
  const node = RESEARCH_NODES.find((n) => n.id === id);
  return node?.displayName ?? id;
}

function formatNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} M`;
  if (value >= 10_000) return `${(value / 1000).toFixed(1)} k`;
  return new Intl.NumberFormat("en-US").format(value);
}

function formatDuration(ms: number): string {
  if (ms <= 0) return "0s";
  const sec = Math.ceil(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rsec = sec % 60;
  if (min < 60) return `${min}m ${rsec.toString().padStart(2, "0")}s`;
  const h = Math.floor(min / 60);
  const rmin = min % 60;
  return `${h}h ${rmin.toString().padStart(2, "0")}m`;
}
